import {
  dateIsValid,
  formatDate,
  neutralizeDate,
  normalizeDate,
  normalizeMonthYear
} from '@/utils/normalize'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDatePicker from 'react-datepicker'
import { format, isValid, parse } from 'date-fns'

// import { Container } from './styles';

const DateView: React.FC<{
  title?: string
  value: any
  onChange?: (v: any) => void
  onBlur?: () => void
  filterDate?: (date: Date) => boolean
  considerHour?: boolean
  minDate?: Date
  maxDate?: Date
  disabled?: boolean
  required?: boolean
  showMonths?: boolean
}> = ({
  title,
  value,
  onChange,
  onBlur = () => {
    //
  },
  filterDate,
  considerHour = false,
  disabled,
  required = false,
  minDate,
  maxDate,
  showMonths
}) => {
  const pattern = showMonths ? 'MM/yyyy' : 'dd/MM/yyyy'

  // * O react-datepicker dá prioridade à prop `value` sobre o próprio estado interno, então é
  // * este texto que aparece no campo — `selected` só alimenta o calendário.
  const [valueRaw, setValueRaw] = useState(() => formatRaw(value, pattern))

  const pickerRef = useRef<any>(null)

  // * Último texto em que campo e `value` estiveram de acordo. É o que separa a mudança vinda da
  // * própria digitação (já refletida no campo) da que vem de fora — filtro salvo aplicado,
  // * "Limpar Filtros", valor da URL —, que precisa sobrescrever o que está escrito.
  const syncedRef = useRef(formatRaw(value, pattern))

  useEffect(() => {
    const next = formatRaw(value, pattern)

    if (next === syncedRef.current) {
      return
    }

    syncedRef.current = next
    setValueRaw(next)
  }, [value, pattern])

  /** As mesmas restrições que o calendário aplica ao clique. */
  const isSelectable = useCallback(
    (date: Date) => {
      if (minDate && startOfDay(date) < startOfDay(neutralizeDate(minDate))) {
        return false
      }
      if (maxDate && startOfDay(date) > startOfDay(neutralizeDate(maxDate))) {
        return false
      }
      return filterDate ? filterDate(date) : true
    },
    [minDate, maxDate, filterDate]
  )

  // * Assume a leitura do que é digitado. Sem isto o react-datepicker completa por conta própria
  // * o que ainda está pela metade, a cada tecla — "1" vira o dia 1 do mês à vista, "15/08/2" vira
  // * o ano 0002 — e devolve essa data ao pai, que reescreve o campo por cima de quem digita.
  const handleChangeRaw = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const typed = event?.target?.value

      // O calendário também passa por aqui, com o evento do clique: só o input traz texto.
      if (typeof typed !== 'string') {
        return
      }

      event.preventDefault()

      const text = showMonths ? normalizeMonthYear(typed) : normalizeDate(typed)
      setValueRaw(text)

      if (!text) {
        syncedRef.current = ''
        onChange?.(null)
        return
      }

      const parsed = parseTyped(text, pattern)

      if (!parsed || !isSelectable(parsed)) {
        return
      }

      // A data já valendo não precisa ser reenviada — evita rebuscar a listagem à toa.
      if (formatRaw(value, pattern) === text) {
        syncedRef.current = text
        return
      }

      syncedRef.current = text
      pickerRef.current?.setPreSelection?.(parsed)
      onChange?.(parsed)
    },
    [showMonths, pattern, value, isSelectable, onChange]
  )

  // * Ao sair do campo o texto volta a ser a data que está valendo: o que ficou pela metade, ou
  // * a data que o calendário não aceita (fora do mínimo/máximo), não fica passando por filtro.
  const normalizeInput = useCallback(() => {
    setValueRaw(formatRaw(value, pattern))
  }, [value, pattern])

  if (!onChange) {
    return <>{value ? formatDate(value, considerHour) : ''}</>
  }
  return (
    <ReactDatePicker
      ref={pickerRef}
      wrapperClassName="d-block"
      title={title}
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      locale="br"
      filterDate={filterDate}
      selected={
        value && dateIsValid(new Date(value))
          ? neutralizeDate(new Date(value))
          : undefined
      }
      value={valueRaw}
      onChangeRaw={handleChangeRaw}
      maxDate={maxDate ? neutralizeDate(maxDate) : undefined}
      minDate={minDate ? neutralizeDate(minDate) : undefined}
      onChange={e => {
        onChange(e ? new Date((e as Date).setHours(0)) : null)
        if (!e) {
          syncedRef.current = ''
          setValueRaw('')
        }
      }}
      onBlur={normalizeInput}
      onCalendarClose={() => {
        normalizeInput()
        onBlur()
      }}
      dateFormat={showMonths ? 'MM/yyyy' : 'dd/MM/yyyy'}
      showMonthYearPicker={showMonths}
      showFullMonthYearPicker={showMonths}
      placeholderText={showMonths ? 'MM/AAAA' : 'DD/MM/AAAA'}
      className="form-control"
      disabled={disabled}
      required={required}
      isClearable
      clearButtonTitle="Limpar data"
    />
  )
}

/** O texto do campo para uma data: vazio quando não há data válida. */
function formatRaw(value: any, pattern: string): string {
  if (!value) {
    return ''
  }

  const date = neutralizeDate(new Date(value))

  return isValid(date) ? format(date, pattern) : ''
}

/**
 * A data de um texto digitado por inteiro — `null` enquanto está pela metade ou não existe no
 * calendário (31/02, mês 13). Reformatar e comparar é o que descarta estas duas últimas: o
 * date-fns aceita o que não bate com o padrão e devolve outro dia.
 */
function parseTyped(text: string, pattern: string): Date | null {
  if (!text || text.length !== pattern.length) {
    return null
  }

  const reference = new Date()
  reference.setHours(0, 0, 0, 0)

  const parsed = parse(text, pattern, reference)

  return isValid(parsed) && format(parsed, pattern) === text ? parsed : null
}

function startOfDay(date: Date): number {
  return new Date(date).setHours(0, 0, 0, 0)
}

export default DateView
